import {Euler, Object3D, Vector3} from "three";
import Action from '../classes/Action';
import DefaultSceneManager from '../classes/DefaultSceneManager';
import { CustomEventTypes, SaveModificationTypes, Space, TimelineEffectorTypes } from '../globals';
import Scenario from "../classes/scenarioElements/Scenario";
import ProjectSaver from '../classes/ProjectSaver';
import MathUtils from '../classes/utilities/MathUtils';
import {ActionDescription, ActionParameters} from "../classes/api/APIDecorators";
import store from '../state/store';
import {RegisterActionWithAPI} from "../classes/api/Decorators";

@ActionDescription("Sets the transform of the selected objects. ")
@ActionParameters({objects: "Object3D[]", newPositions: "Vector3[]", oldPositions: "Vector3[]", newRotations: "Euler[]", oldRotations: "Euler[]", newScales: "Vector3[]", oldScales: "Vector3[]"})
@RegisterActionWithAPI("SetObjectTransformAction")
export default class SetObjectTransformAction extends Action
{
	constructor(objects: Object3D[], newPositions: Vector3[], oldPositions: Vector3[], newRotations: Euler[], oldRotations: Euler[], newScales: Vector3[], oldScales: Vector3[])
	{
		const actionName = "Paste Transform";
		const isDefaultState = Scenario.currentStep()?.isDefault();

		super([objects, newPositions, newRotations, newScales, isDefaultState],
			[objects, oldPositions, oldRotations, oldScales, isDefaultState],
			actionName,
			[objects, newPositions, oldPositions, newRotations, oldRotations, newScales, oldScales]);
	}

	do(objects: Object3D[], positions: Vector3[], rotations: Euler[], scales: Vector3[], isDefaultState: boolean)
	{
		if (isDefaultState)
		{
			DefaultSceneManager.getInstance().syncNativeObjectVectorProperty(objects, 'position', positions);
			DefaultSceneManager.getInstance().syncNativeObjectVectorProperty(objects, 'rotation', rotations);
			DefaultSceneManager.getInstance().syncNativeObjectVectorProperty(objects, 'scale', scales);
		}

		for (let i = 0; i < objects.length; i++)
		{
			const object = objects[i];
			object.position.copy(positions[i]);
			object.rotation.copy(rotations[i]);
			object.scale.copy(scales[i]);
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Position);
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Rotation);
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Scale);
		}

		if (store.getState().timelineModule.editorData.isRecording)
		{
			const translationEventDetail = { animationType: TimelineEffectorTypes.TransformTranslate, space: Space.Local };
			const rotationEventDetail = { animationType: TimelineEffectorTypes.TransformRotateSelf };
			const scaleEventDetail = { animationType: TimelineEffectorTypes.TransformScale };
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onTransformRecordEvent, { detail: translationEventDetail }));
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onTransformRecordEvent, { detail: rotationEventDetail }));
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onTransformRecordEvent, { detail: scaleEventDetail }));
		}


		document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectPositionChanged));
		document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectRotationChanged));
		document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectScaleChanged));
	}

	verify(objects: Object3D[], newPositions, oldPositions, newRotations, oldRotations, newScales, oldScales)
	{
		// check if arrays have compatible lengths
		if (objects.length === 0 ||
			objects.length !== newPositions.length ||
			objects.length !== oldPositions.length ||
			objects.length !== newRotations.length ||
			objects.length !== oldRotations.length ||
			objects.length !== newScales.length ||
			objects.length !== oldScales.length
		)
		{return false;}

		// check for difference between old and new
		let difference = false;
		for (let i = 0; i < objects.length; i++)
		{
			if (!MathUtils.areVectorsEqual(newPositions[i], oldPositions[i]))
			{
				difference = true;
				break;
			}
			if (!MathUtils.areVectorsEqual(newRotations[i], oldRotations[i]))
			{
				difference = true;
				break;
			}
			if (!MathUtils.areVectorsEqual(newScales[i], oldScales[i]))
			{
				difference = true;
				break;
			}
		}

		if (difference && store.getState().timelineModule.editorData.isRecording) this.flagAsTemporary();
		return difference;
	}
}
